import { GiCheckMark } from 'react-icons/gi'
import { FaExclamationTriangle } from 'react-icons/fa'
import { HiSelector } from 'react-icons/hi'
import { AnimatePresence, motion } from 'framer-motion'
import { useEffect, useMemo, useRef, useState } from 'react'
import { createPortal } from 'react-dom'
import { AiFillCloseCircle } from 'react-icons/ai'

const SelectOptionAnim = {
  hidden: { opacity: 0, y: 8 },
  visible: { opacity: 1, y: 0 },
}

const DEFAULT_MENU_HEIGHT = 270
const VIEWPORT_PADDING = 12
const DROPDOWN_OFFSET = 8

let multiSelectTime

const FieldMultiSelect = ({
  fieldProps = { value: [] }, fieldMeta = {}, label, edit, options = [], fieldHelper,
  loading = false, dataLoaded = false,
  onChange, disabled = false, injectClass = ""
}) => {
  const input = useRef(null)
  const wrapRef = useRef(null)
  const dropdownRef = useRef(null)

  const [showError, setShowError] = useState(false)
  const [filter, setFilter] = useState('')
  const [focussed, setFocussed] = useState(false)
  const [dropdownStyle, setDropdownStyle] = useState({})

  const hasError = Boolean(fieldMeta.error && fieldMeta.touched)

  const toggleFocus = () => setFocussed(prev => !prev)
  const makeFocus = () => {
    if (disabled || !edit) return
    input.current?.focus()
  }

  const handleBlur = () => {
    multiSelectTime = setTimeout(() => {
      if (focussed) toggleFocus()
    }, 200)
  }

  useEffect(() => {
    const updateDropdownPosition = () => {
      const wrap = wrapRef.current
      if (!wrap) return

      const rect = wrap.getBoundingClientRect()
      const availableHeight = Math.max(
        96,
        window.innerHeight - rect.bottom - VIEWPORT_PADDING - DROPDOWN_OFFSET
      )

      setDropdownStyle({
        position: 'fixed',
        width: `${Math.max(160, rect.width)}px`,
        left: `${Math.max(VIEWPORT_PADDING, rect.left)}px`,
        top: `${rect.bottom + DROPDOWN_OFFSET}px`,
        maxHeight: `${Math.min(DEFAULT_MENU_HEIGHT, availableHeight)}px`,
      })
    }

    if (focussed) updateDropdownPosition()

    const handleScrollResize = () => {
      if (focussed) updateDropdownPosition()
    }

    window.addEventListener('scroll', handleScrollResize, true)
    window.addEventListener('resize', handleScrollResize)

    const handleClickOutside = (event) => {
      if (
        dropdownRef.current &&
        !dropdownRef.current.contains(event.target) &&
        wrapRef.current &&
        !wrapRef.current.contains(event.target)
      ) {
        setFocussed(false)
      }
    }

    document.addEventListener('mousedown', handleClickOutside)

    return () => {
      window.removeEventListener('scroll', handleScrollResize, true)
      window.removeEventListener('resize', handleScrollResize)
      document.removeEventListener('mousedown', handleClickOutside)
    }
  }, [focussed])

  const setValue = (ev, value) => {
    ev.stopPropagation()
    clearTimeout(multiSelectTime)

    let newValue = [...(fieldProps.value || [])]
    if (newValue.includes(value)) {
      newValue = newValue.filter(c => c !== value)
    } else {
      newValue.push(value)
    }

    if (value === 9) {
      newValue = [9]
    } else if (value !== 9 && newValue.includes(9)) {
      newValue = newValue.filter(c => c !== 9).concat(value)
    }

    if (fieldHelper?.setValue) fieldHelper.setValue(newValue)
    if (typeof onChange === 'function') onChange(newValue)
  }

  const removeItem = (ev, val) => {
    ev.stopPropagation()
    if (fieldHelper?.setValue) fieldHelper.setValue((fieldProps.value || []).filter(c => c !== val))
  }

  const filteredOptions = useMemo(() => {
    return options.filter(c => !filter || c.text.toLowerCase().includes(filter.toLowerCase().trim()))
  }, [filter, options])

  const selectedValues = useMemo(() => {
    return (fieldProps.value || [])
      .map(c => options.find(e => e.value.toString() === c.toString()))
      .filter(Boolean)
  }, [fieldProps.value, options])

  return (
    <motion.div className="relative mt-3 mb-2">
      {label ? <div className="mb-2 px-1 text-xs font-bold uppercase tracking-[0.18em] text-tpColor">{label}</div> : null}

      <motion.div className={`relative w-full rounded-[22px] border border-borderColor bg-white/92 px-4 py-3 shadow-soft ${injectClass}`}>
        <div
          ref={wrapRef}
          className={`group relative flex flex-col rounded-lg ${disabled ? "cursor-not-allowed opacity-60" : "cursor-pointer"} transition-all duration-100 ${loading ? 'animate-pulse' : ''}`}
          onClick={makeFocus}
        >
          {!dataLoaded ? (
            <div className="h-6 w-3/4 animate-pulse rounded-lg bg-slate-200"></div>
          ) : (
            <>
              {selectedValues.length > 0 && (
                <div className="mb-2 flex flex-wrap gap-2">
                  {selectedValues.map((item) => (
                    <span key={item.value} className="inline-flex items-center gap-2 rounded-full bg-primeColor/10 px-3 py-1 text-xs font-bold text-primeColor">
                      {item.text}
                      <button type="button" onClick={(ev) => removeItem(ev, item.value)}>
                        <AiFillCloseCircle className="text-sm text-primeColor/80 transition hover:text-primeDark" />
                      </button>
                    </span>
                  ))}
                </div>
              )}

              {edit && (
                <>
                  <input
                    ref={input}
                    className="w-full bg-transparent py-1 pr-10 text-sm font-semibold text-tColor outline-none placeholder:text-tpColor/50"
                    type="text"
                    value={filter}
                    autoComplete="off"
                    onFocus={toggleFocus}
                    onBlur={handleBlur}
                    onChange={(ev) => setFilter(ev.target.value)}
                    placeholder={`Search ${label || 'options'}`}
                    disabled={disabled}
                  />
                  <span className="absolute right-1 top-1 flex text-xl">
                    {hasError && (
                      <FaExclamationTriangle
                        className="cursor-pointer text-red-500"
                        onMouseEnter={() => setShowError(true)}
                        onMouseLeave={() => setShowError(false)}
                      />
                    )}
                    <HiSelector className={`ml-2 text-gray-400 transition ${focussed ? 'rotate-180' : ''}`} />
                  </span>
                </>
              )}
            </>
          )}

          {showError && (
            <motion.div className="absolute right-0 top-8 z-20 w-56 rounded-2xl border border-rose-200 bg-white px-3 py-2 text-xs font-semibold leading-5 text-[#c44545] shadow-soft">
              {fieldMeta.error}
            </motion.div>
          )}
        </div>
      </motion.div>

      {createPortal(
        <AnimatePresence>
          {focussed && (
            <motion.div
              ref={dropdownRef}
              variants={SelectOptionAnim}
              initial="hidden"
              animate="visible"
              exit="hidden"
              className="fixed z-[200000] overflow-y-auto rounded-[22px] border border-slate-200 bg-white p-2 shadow-[0_24px_60px_-24px_rgba(15,23,42,0.45)] backdrop-blur-0"
              style={dropdownStyle}
            >
              {filteredOptions.map((option, index) => (
                <button
                  key={index}
                  type="button"
                  onClick={(ev) => {
                    setFilter("")
                    setValue(ev, option.value)
                  }}
                  className="flex w-full items-center justify-between rounded-2xl px-3 py-3 text-left text-sm font-semibold text-tColor transition hover:bg-primeColor hover:text-white"
                >
                  <span>{option.text}</span>
                  {(fieldProps.value || []).includes(option.value) && (
                    <GiCheckMark className="text-green-500" />
                  )}
                </button>
              ))}
              {filteredOptions.length === 0 && <div className="px-4 py-3 text-sm font-semibold text-tpColor">No options found</div>}
            </motion.div>
          )}
        </AnimatePresence>,
        document.body
      )}
    </motion.div>
  )
}

export default FieldMultiSelect
